import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [children_, setChildren] = useState([]);

    const api = useCallback(() => {
        const instance = axios.create({
            baseURL: API,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        return instance;
    }, [token]);

    useEffect(() => {
        const fetchUser = async () => {
            if (token) {
                try {
                    const response = await api().get('/auth/me');
                    setUser(response.data);
                } catch (error) {
                    console.error('Auth error:', error);
                    localStorage.removeItem('token');
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };
        fetchUser();
    }, [token, api]);

    const login = async (email, password) => {
        const response = await api().post('/auth/login', { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await api().post('/auth/register', { email, password, name });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setChildren([]);
    };

    const createChild = async (name, age, avatar) => {
        const response = await api().post('/auth/child', { name, age, avatar });
        setChildren(prev => [...prev, response.data]);
        return response.data;
    };

    const fetchChildren = async () => {
        const response = await api().get('/auth/children');
        setChildren(response.data);
        return response.data;
    };

    const switchToChild = async (childId) => {
        const response = await api().post(`/auth/switch-child/${childId}`);
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const updateUser = (updates) => {
        setUser(prev => ({ ...prev, ...updates }));
    };

    const updatePassword = async (currentPassword, newPassword) => {
        await api().post('/auth/update-password', { current_password: currentPassword, new_password: newPassword });
    };

    const updateEmail = async (newEmail, currentPassword) => {
        const res = await api().post('/auth/update-email', { new_email: newEmail, current_password: currentPassword });
        setUser(prev => ({ ...prev, email: res.data.email, email_verified: false }));
        return res.data;
    };

    const verifyEmailCode = async (code) => {
        await api().post('/auth/verify-email', { code });
        setUser(prev => ({ ...prev, email_verified: true }));
    };

    const resendVerification = async () => {
        await api().post('/auth/resend-verification');
    };

    const value = {
        user,
        token,
        loading,
        children: children_,
        api,
        login,
        register,
        logout,
        createChild,
        fetchChildren,
        switchToChild,
        updateUser,
        updatePassword,
        updateEmail,
        verifyEmailCode,
        resendVerification,
        isAuthenticated: !!user,
        isParent: user?.role === 'parent',
        isChild: user?.role === 'child',
        isAdmin: user?.role === 'admin',
        isEducator: user?.role === 'educator'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
